import { EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  message,
  Row,
  Select,
  Tooltip
} from "antd";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import React, { useEffect, useState } from "react";
import API from "../../../api";
import CustomTable from "../../../components/Table/CustomTable";
import CustomerCancelledPaymentDrawer from "./PaymentCollectionDrawer";

// Extend Day.js with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;
const { Option } = Select;

const CustomerCancelledPayment = () => {
  const [form] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [paymentsData, setPaymentsData] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Ensure it's null initially
  const [customers, setCustomers] = useState([]);
  const [customerData, setCustomerData] = useState({
    openingBalance: 0,
    currentBalance: 0,
    closingBalance: 0,
  });
  const [selectedDates, setSelectedDates] = useState([]);

  const [filters, setFilters] = useState({
    storeId: "",
    search: "",
    fromDate: null,
    toDate: null,
  });

  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const handleDateChange = (dates) => {
    setSelectedDates(
      dates
        ? [dates[0]?.format("YYYY-MM-DD"), dates[1]?.format("YYYY-MM-DD")]
        : []
    );
  };

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value || null,
    }));
  };

  const handleFilter = () => {
    if (selectedDates.length === 2) {
      handleSearchChange("fromDate", selectedDates[0]);
      handleSearchChange("toDate", selectedDates[1]);
    } else {
      handleSearchChange("fromDate", null);
      handleSearchChange("toDate", null);
    }
  };

  const handleReset = () => {
    setSelectedDates([]);
    handleSearchChange("fromDate", null);
    handleSearchChange("toDate", null);
    setSelectedCustomer(null); // Ensure selected customer is reset
  };

  const columns = [
    {
      title: "Sl. No",
      dataIndex: "slNo",
      key: "slNo",
      render: (text, record, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1, // Adjust serial number based on pagination
    },
    {
      title: "Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      // render: (text) => new Date(text)?.toLocaleDateString(),
      render: (text) => {
        const date = new Date(text);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      }

    },
    !selectedCustomer && {
      title: "Customer Name",
      dataIndex: ["customerId", "name"],
      key: "transactionId",
    },
    {
      title: "Inv No",
      dataIndex: "transactionId",
      key: "transactionId",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => text?.toUpperCase(),
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (text) => text?.toUpperCase(),
    },
    {
      title: "Inv Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (text) => `₹${text?.toLocaleString()}`,
    },
    {
      title: "Paid",
      dataIndex: "paid_amount",
      key: "paid_amount",
      render: (text) => `₹${text?.toLocaleString()}`,
    },
    {
      title: "Balance",
      dataIndex: "balance_amount",
      key: "balance_amount",
      render: (text) => `₹${text?.toLocaleString()}`,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (text) => <span style={{ color: text === "cancelled" ? "#ff4d4f" : "inherit" }}>
        {text || "N/A"}
      </span>
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <>
          {
            record.type === 'pay' &&
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
              type="link"
            />
          }
          {
            record.type === 'inv' &&
            <Tooltip title="This Payment is not editable from here, Go to particular 'Sale' to Update this">
              <Button
                icon={<InfoCircleOutlined />}
                type="link"
              />
            </Tooltip>
          }
        </>
      ),
    },
  ];

  const handleEditClick = (record) => {
    setSelectedPayment(record);
    setDrawerVisible(true);
  };
  const fetchCustomers = async () => {
    try {
      const response = await API.get("/customers");
      setCustomers(response.data || []);
      if (response.data.length > 0) {
      }
    } catch (error) {
      message.error("Failed to fetch customers.");
      console.error("Error fetching customers:", error);
    }
  };

  useEffect(() => {
    fetchPayments();

  }, [selectedCustomer, paginationInfo.current, paginationInfo.pageSize, filters]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      let response;
      const { search, fromDate, toDate } = filters;
        response = await API.get(`/payments/customers/cancelled`, {
          params: {
            page: paginationInfo.current,
            limit: paginationInfo.pageSize,
            search,
            fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
            toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
          },
        });
      const { pagination, payments, totals } = response.data;
      setPaymentsData(payments || []);

      setCustomerData({
        openingBalance: totals?.totalAmount || 0,
        currentBalance: totals?.paidAmount || 0,
        closingBalance: totals?.balanceAmount || 0,
      });

      setPaginationInfo((prev) => ({ ...prev, total: pagination?.total }));
    } catch (error) {
      message.error("Failed to fetch data.");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };


  const openDrawer = () => {
    setDrawerVisible(true);
  };


  useEffect(() => {
    fetchCustomers();
  }, []);


  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      {/* <Row justify="space-between" align="middle" style={{ marginBottom: "20px" }}>
      <Col xs={24} sm={12} md={6}>
        <h2 style={{ marginBottom: "10px", fontSize: "20px", color: "#1a1a1a" }}>Cancelled Payments</h2>         
        </Col>
      </Row> */}


      <Row gutter={16}>
        <Col xs={24}>
          <Card title={
            <Row justify="space-between" align="middle" style={{ width: '100%' }}>
              <Col>                
                <h2 style={{ marginBottom: "10px", fontSize: "20px", color: "#1a1a1a" }}>Cancelled Transactions</h2>               </Col>
              <Col style={{ textAlign: 'right' }}>
                <div>
                  <span>
                    Total Remaining:
                    <span style={{ color: customerData.openingBalance >= 0 ? 'green' : 'red' }}>
                      ₹{customerData.openingBalance.toLocaleString()}
                    </span>
                  </span>
                  <br />
                  <span>
                    Paid Balance:
                    <span style={{ color: customerData.currentBalance >= 0 ? 'green' : 'red' }}>
                      ₹{customerData.currentBalance.toLocaleString()}
                    </span>
                  </span>
                  <br />
                  <span>
                    Remaining Balance:
                    <span style={{ color: customerData.closingBalance >= 0 ? 'green' : 'red' }}>
                      ₹{customerData.closingBalance.toLocaleString()}
                    </span>
                  </span>
                </div>



              </Col>
            </Row>
          } bordered={false}>

            <CustomTable
              columns={columns}
              dataSource={paymentsData}
              onPaginationChange={(pagination) => {
                setPaginationInfo({
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                });
              }}
              loading={loading}
              style={{ padding: "0 0px" }}
              total={paginationInfo.total}
            />
          </Card>
        </Col>
      </Row>


      <CustomerCancelledPaymentDrawer
        open={drawerVisible}
        setDrawerVisible={setDrawerVisible}
        handleSubmit={() => fetchPayments()}
        selectedPayment={selectedPayment != null ? selectedPayment : null}
      />
    </div >
  );
};

export default CustomerCancelledPayment;
